/**
 * Premium Glass Theme – Core JavaScript
 * Lightweight, no dependencies
 */

(function () {
  'use strict';

  // --------------------------------------------------------------------------
  // Utilities
  // --------------------------------------------------------------------------
  const qs = (sel, ctx = document) => ctx.querySelector(sel);
  const qsa = (sel, ctx = document) => [...ctx.querySelectorAll(sel)];

  function debounce(fn, wait = 150) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn(...args), wait);
    };
  }

  function formatMoney(cents, format) {
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
      return Shopify.formatMoney(cents, format || Shopify.money_format || '${{amount}}');
    }
    return (cents / 100).toFixed(2);
  }

  // --------------------------------------------------------------------------
  // Cart Drawer
  // --------------------------------------------------------------------------
  const CartDrawer = {
    overlay: null,
    drawer: null,
    isOpen: false,

    init() {
      this.overlay = qs('[data-cart-overlay]');
      this.drawer = qs('[data-cart-drawer]');
      if (!this.drawer) return;

      // Open triggers
      qsa('[data-cart-open]').forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          this.open();
        });
      });

      // Close triggers
      qsa('[data-cart-close]').forEach(btn => {
        btn.addEventListener('click', () => this.close());
      });

      if (this.overlay) {
        this.overlay.addEventListener('click', () => this.close());
      }

      // Escape key
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen) this.close();
      });

      // Listen for cart updates
      document.addEventListener('cart:updated', () => this.refresh());
    },

    open() {
      if (!this.drawer) return;
      this.isOpen = true;
      this.drawer.classList.add('is-open');
      if (this.overlay) this.overlay.classList.add('is-open');
      document.body.style.overflow = 'hidden';
      this.refresh();
    },

    close() {
      if (!this.drawer) return;
      this.isOpen = false;
      this.drawer.classList.remove('is-open');
      if (this.overlay) this.overlay.classList.remove('is-open');
      document.body.style.overflow = '';
    },

    async refresh() {
      try {
        const res = await fetch('/cart.js');
        const cart = await res.json();
        this.render(cart);
        this.updateCount(cart.item_count);
      } catch (err) {
        console.error('Cart refresh failed', err);
      }
    },

    updateCount(count) {
      qsa('[data-cart-count]').forEach(el => {
        el.textContent = count;
        el.style.display = count > 0 ? '' : 'none';
      });
    },

    render(cart) {
      const body = qs('[data-cart-body]', this.drawer);
      const footer = qs('[data-cart-footer]', this.drawer);
      const empty = qs('[data-cart-empty]', this.drawer);
      if (!body) return;

      if (cart.item_count === 0) {
        body.innerHTML = '';
        if (empty) empty.hidden = false;
        if (footer) footer.hidden = true;
        return;
      }

      if (empty) empty.hidden = true;
      if (footer) footer.hidden = false;

      body.innerHTML = cart.items.map(item => `
        <div class="cart-item" data-key="${item.key}">
          <div class="cart-item__image">
            <img src="${item.image || ''}" alt="${item.product_title}" width="80" height="80" loading="lazy">
          </div>
          <div class="cart-item__details">
            <h3 class="cart-item__title">${item.product_title}</h3>
            ${item.variant_title && item.variant_title !== 'Default Title' ? `<p class="cart-item__variant">${item.variant_title}</p>` : ''}
            <p class="cart-item__price">${formatMoney(item.final_line_price)}</p>
            <div class="cart-item__actions">
              <div class="quantity">
                <button type="button" class="quantity__btn" data-qty-change="-1" data-key="${item.key}" aria-label="Decrease quantity">−</button>
                <input type="number" class="quantity__input" value="${item.quantity}" min="0" data-key="${item.key}" aria-label="Quantity">
                <button type="button" class="quantity__btn" data-qty-change="1" data-key="${item.key}" aria-label="Increase quantity">+</button>
              </div>
              <button type="button" class="cart-item__remove" data-remove="${item.key}">Remove</button>
            </div>
          </div>
        </div>
      `).join('');

      // Subtotal
      const subtotalEl = qs('[data-cart-subtotal]', this.drawer);
      if (subtotalEl) subtotalEl.textContent = formatMoney(cart.total_price);

      // Bind quantity & remove
      body.querySelectorAll('[data-qty-change]').forEach(btn => {
        btn.addEventListener('click', () => {
          const key = btn.dataset.key;
          const input = body.querySelector(`.quantity__input[data-key="${key}"]`);
          const delta = parseInt(btn.dataset.qtyChange, 10);
          const newQty = Math.max(0, parseInt(input.value, 10) + delta);
          this.updateItem(key, newQty);
        });
      });

      body.querySelectorAll('.quantity__input').forEach(input => {
        input.addEventListener('change', () => {
          this.updateItem(input.dataset.key, parseInt(input.value, 10) || 0);
        });
      });

      body.querySelectorAll('[data-remove]').forEach(btn => {
        btn.addEventListener('click', () => this.updateItem(btn.dataset.remove, 0));
      });
    },

    async updateItem(key, quantity) {
      try {
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: key, quantity })
        });
        document.dispatchEvent(new CustomEvent('cart:updated'));
      } catch (err) {
        console.error('Update item failed', err);
      }
    }
  };

  // --------------------------------------------------------------------------
  // Add to Cart (forms & quick add)
  // --------------------------------------------------------------------------
  async function addToCart(formOrData, openDrawer = true) {
    let body;
    if (formOrData instanceof HTMLFormElement) {
      body = new FormData(formOrData);
    } else {
      body = JSON.stringify(formOrData);
    }

    const headers = formOrData instanceof HTMLFormElement
      ? {}
      : { 'Content-Type': 'application/json' };

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers,
        body
      });
      if (!res.ok) throw new Error('Add failed');
      document.dispatchEvent(new CustomEvent('cart:updated'));
      if (openDrawer && window.themeSettings?.cartDrawerEnabled !== false) {
        CartDrawer.open();
      }
      return true;
    } catch (err) {
      console.error(err);
      return false;
    }
  }

  // Product form submit
  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('form[data-product-form]');
    if (!form) return;
    e.preventDefault();
    const btn = form.querySelector('[type="submit"]');
    if (btn) {
      btn.disabled = true;
      btn.textContent = 'Adding…';
    }
    await addToCart(form);
    if (btn) {
      btn.disabled = false;
      btn.textContent = btn.dataset.originalText || 'Add to cart';
    }
  });

  // Quick add buttons
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-quick-add]');
    if (!btn) return;
    e.preventDefault();
    const id = btn.dataset.quickAdd;
    if (!id) return;
    btn.disabled = true;
    await addToCart({ id, quantity: 1 });
    btn.disabled = false;
  });

  // --------------------------------------------------------------------------
  // Product Variant Selection
  // --------------------------------------------------------------------------
  function initProductForms() {
    qsa('[data-product-form]').forEach(form => {
      const productJson = qs('[data-product-json]', form.closest('[data-product]'));
      if (!productJson) return;

      let product;
      try {
        product = JSON.parse(productJson.textContent);
      } catch {
        return;
      }

      const optionBtns = qsa('[data-option-value]', form);
      const priceEl = qs('[data-product-price]', form.closest('[data-product]'));
      const compareEl = qs('[data-product-compare]', form.closest('[data-product]'));
      const availabilityEl = qs('[data-product-availability]', form.closest('[data-product]'));
      const idInput = qs('[name="id"]', form);
      const addBtn = qs('[type="submit"]', form);

      function getSelectedOptions() {
        const selected = {};
        optionBtns.forEach(btn => {
          if (btn.classList.contains('is-selected')) {
            selected[btn.dataset.optionPosition] = btn.dataset.optionValue;
          }
        });
        return selected;
      }

      function findVariant(selected) {
        return product.variants.find(v => {
          return v.options.every((opt, i) => selected[String(i + 1)] === opt);
        });
      }

      function updateUI(variant) {
        if (!variant) {
          if (addBtn) {
            addBtn.disabled = true;
            addBtn.textContent = 'Unavailable';
          }
          if (availabilityEl) {
            availabilityEl.textContent = 'Unavailable';
            availabilityEl.classList.add('product__availability--out');
          }
          return;
        }

        if (idInput) idInput.value = variant.id;

        if (priceEl) {
          priceEl.innerHTML = formatMoney(variant.price);
          if (variant.compare_at_price > variant.price) {
            priceEl.classList.add('price__sale');
          } else {
            priceEl.classList.remove('price__sale');
          }
        }

        if (compareEl) {
          if (variant.compare_at_price > variant.price) {
            compareEl.textContent = formatMoney(variant.compare_at_price);
            compareEl.hidden = false;
          } else {
            compareEl.hidden = true;
          }
        }

        if (addBtn) {
          addBtn.disabled = !variant.available;
          addBtn.textContent = variant.available ? (addBtn.dataset.originalText || 'Add to cart') : 'Sold out';
        }

        if (availabilityEl) {
          availabilityEl.textContent = variant.available ? 'In stock' : 'Sold out';
          availabilityEl.classList.toggle('product__availability--out', !variant.available);
        }

        // Update URL
        const url = new URL(window.location);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);
      }

      optionBtns.forEach(btn => {
        btn.addEventListener('click', () => {
          const position = btn.dataset.optionPosition;
          // Deselect siblings
          qsa(`[data-option-position="${position}"]`, form).forEach(b => b.classList.remove('is-selected'));
          btn.classList.add('is-selected');

          const selected = getSelectedOptions();
          const variant = findVariant(selected);
          updateUI(variant);
        });
      });

      // Init from current variant or first available
      const currentId = idInput ? idInput.value : null;
      let current = product.variants.find(v => String(v.id) === String(currentId));
      if (!current) current = product.variants.find(v => v.available) || product.variants[0];
      if (current) {
        current.options.forEach((opt, i) => {
          const btn = qs(`[data-option-position="${i + 1}"][data-option-value="${opt}"]`, form);
          if (btn) btn.classList.add('is-selected');
        });
        updateUI(current);
      }
    });
  }

  // --------------------------------------------------------------------------
  // Mobile Menu
  // --------------------------------------------------------------------------
  function initMobileMenu() {
    const toggle = qs('[data-mobile-menu-toggle]');
    const menu = qs('[data-mobile-menu]');
    const closeBtns = qsa('[data-mobile-menu-close]');

    if (!toggle || !menu) return;

    toggle.addEventListener('click', () => {
      menu.classList.add('is-open');
      document.body.style.overflow = 'hidden';
    });

    closeBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        menu.classList.remove('is-open');
        document.body.style.overflow = '';
      });
    });
  }

  // --------------------------------------------------------------------------
  // Product Gallery (simple)
  // --------------------------------------------------------------------------
  function initGallery() {
    qsa('[data-product-gallery]').forEach(gallery => {
      const main = qs('[data-gallery-main]', gallery);
      const thumbs = qsa('[data-gallery-thumb]', gallery);
      if (!main || !thumbs.length) return;

      thumbs.forEach(thumb => {
        thumb.addEventListener('click', () => {
          const src = thumb.dataset.src || thumb.querySelector('img')?.src;
          if (src && main.querySelector('img')) {
            main.querySelector('img').src = src;
            thumbs.forEach(t => t.classList.remove('is-active'));
            thumb.classList.add('is-active');
          }
        });
      });
    });
  }

  // --------------------------------------------------------------------------
  // Init
  // --------------------------------------------------------------------------
  function init() {
    CartDrawer.init();
    initProductForms();
    initMobileMenu();
    initGallery();

    // Store original button text
    qsa('[data-product-form] [type="submit"]').forEach(btn => {
      btn.dataset.originalText = btn.textContent.trim();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Expose for external use
  window.PremiumGlass = { CartDrawer, addToCart };
})();
